import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-documents',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-[1400px] mx-auto space-y-5">
        <header class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Documents</h1>
            <p class="text-slate-500 text-xs mt-1">Create folders for shared company documents.</p>
          </div>
          <button type="button" (click)="openFolderModal()" class="h-10 px-4 bg-[#0C3A5A] text-white rounded-lg text-xs font-bold hover:bg-[#082d46] flex items-center gap-2">
            <i class="ti ti-folder-plus text-base"></i>New Folder
          </button>
        </header>

        <section class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div class="summary-card"><p>Folders</p><strong>{{ folders().length }}</strong></div>
          <div class="summary-card"><p>Documents</p><strong>{{ documents().length }}</strong></div>
          <div class="summary-card"><p>Storage</p><strong class="text-slate-700">{{ totalSizeLabel() }}</strong></div>
        </section>

        <section class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
            <div class="relative flex-1 max-w-md">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
              <input type="text" placeholder="Search folders..." [(ngModel)]="searchQuery"
                     class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:bg-white focus:border-[#1A6FA6]">
            </div>
          </div>

          @if (isLoading()) {
            <div class="p-10 text-center text-sm text-slate-500"><i class="ti ti-loader animate-spin"></i> Loading folders...</div>
          } @else {
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
              @for (folder of filteredFolders(); track folder.id) {
                <div class="rounded-xl border border-slate-200 bg-white p-4 hover:border-[#BEE7F9] hover:bg-[#F8FCFE] transition-colors">
                  <a [routerLink]="['/admin/documents', folder.id]" class="block">
                    <div class="flex items-start gap-3">
                      <span class="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 border border-amber-100 flex items-center justify-center shrink-0">
                        <i class="ti ti-folder text-xl"></i>
                      </span>
                      <span class="min-w-0 flex-1">
                        <span class="block text-sm font-semibold text-slate-900 truncate">{{ folder.name }}</span>
                        <span class="block text-xs text-slate-500 mt-1 line-clamp-2 min-h-[32px]">{{ folder.description || 'No description' }}</span>
                        <span class="block text-[11px] font-semibold text-slate-400 mt-2">{{ folder.document_count || 0 }} document(s)</span>
                      </span>
                    </div>
                  </a>
                  <div class="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                    <button type="button" (click)="openFolderModal(folder)" class="h-8 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50">
                      <i class="ti ti-pencil"></i>Edit
                    </button>
                    <button type="button" (click)="deleteFolder(folder)" class="h-8 px-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold hover:bg-rose-100">
                      <i class="ti ti-trash"></i>Delete
                    </button>
                  </div>
                </div>
              } @empty {
                <div class="md:col-span-2 xl:col-span-3 p-12 text-center">
                  <i class="ti ti-folder-plus text-4xl text-slate-300"></i>
                  <p class="text-sm font-semibold text-slate-900 mt-2">No folders found</p>
                  <p class="text-xs text-slate-500 mt-1">Create a folder before uploading documents.</p>
                </div>
              }
            </div>
          }
        </section>

        @if (showFolderModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-100 overflow-hidden">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <h2 class="text-lg font-semibold text-slate-900">{{ folderForm.id ? 'Edit Folder' : 'Create Folder' }}</h2>
                <button type="button" (click)="showFolderModal.set(false)" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
              </div>
              <div class="p-5 space-y-4">
                <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Folder Name</span><input type="text" [(ngModel)]="folderForm.name" class="field-input"></label>
                <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Description</span><textarea [(ngModel)]="folderForm.description" rows="3" class="field-input h-auto py-2 resize-none"></textarea></label>
              </div>
              <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button type="button" (click)="showFolderModal.set(false)" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200">Cancel</button>
                <button type="button" (click)="saveFolder()" [disabled]="isSaving() || !folderForm.name.trim()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50">
                  {{ isSaving() ? 'Saving...' : 'Save Folder' }}
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .summary-card { border: 1px solid #E7EEF5; border-radius: 0.75rem; background: #fff; padding: 0.75rem 1rem; }
    .summary-card p { font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.12em; font-weight: 700; color: #94a3b8; }
    .summary-card strong { display: block; margin-top: 0.25rem; font-size: 1.25rem; font-weight: 700; color: #0f172a; }
    .field-input { width: 100%; min-height: 2.5rem; border-radius: 0.5rem; border: 1px solid #e2e8f0; background: #f8fafc; padding: 0 0.75rem; font-size: 0.875rem; color: #1e293b; outline: none; }
    .field-input:focus { border-color: #1A6FA6; background: #fff; }
  `]
})
export class AdminDocumentsComponent implements OnInit {
  private api = inject(ApiService);

  folders = signal<any[]>([]);
  documents = signal<any[]>([]);
  isLoading = signal(true);
  isSaving = signal(false);
  showFolderModal = signal(false);
  searchQuery = '';
  folderForm = { id: 0, name: '', description: '' };

  ngOnInit() {
    this.loadLibrary();
  }

  loadLibrary() {
    this.isLoading.set(true);
    this.api.getDocumentLibrary().subscribe({
      next: (res: any) => {
        this.folders.set(res.success && res.data?.folders ? res.data.folders : []);
        this.documents.set(res.success && res.data?.documents ? res.data.documents : []);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load documents.');
      }
    });
  }

  filteredFolders() {
    const query = this.searchQuery.trim().toLowerCase();
    return this.folders().filter(folder => !query || [folder.name, folder.description].some(value => String(value || '').toLowerCase().includes(query)));
  }

  totalSizeLabel(): string {
    return this.fileSizeLabel(this.documents().reduce((sum, doc) => sum + Number(doc.file_size || 0), 0));
  }

  openFolderModal(folder: any = null) {
    this.folderForm = folder ? { id: Number(folder.id), name: folder.name || '', description: folder.description || '' } : { id: 0, name: '', description: '' };
    this.showFolderModal.set(true);
  }

  saveFolder() {
    if (!this.folderForm.name.trim()) return;
    this.isSaving.set(true);
    this.api.saveDocumentFolder({
      id: this.folderForm.id || undefined,
      name: this.folderForm.name.trim(),
      description: this.folderForm.description || ''
    }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Folder saved.');
          this.showFolderModal.set(false);
          this.loadLibrary();
        } else {
          showToast('error', res.message || 'Failed to save folder.');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Network error while saving folder.');
      }
    });
  }

  deleteFolder(folder: any) {
    if (!folder?.id) return;
    if (!window.confirm(`Delete "${folder.name}" and all documents inside it?`)) return;
    this.api.deleteDocumentFolder(Number(folder.id)).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Folder deleted.');
          this.loadLibrary();
        } else {
          showToast('error', res.message || 'Failed to delete folder.');
        }
      },
      error: (err) => showToast('error', err?.error?.message || 'Network error while deleting folder.')
    });
  }

  fileSizeLabel(size: any): string {
    const bytes = Number(size || 0);
    if (bytes <= 0) return '0 KB';
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
}
